import React from "react";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import { API_BASE_URL } from "../../api/client";
import {
  createGrowthTarget,
  decideGrowthAction,
  deleteGrowthAction,
  deleteGrowthContentAsset,
  getGrowthAudit,
  getGrowthAgents,
  listGrowthContentAssets,
  listGrowthInboxMessages,
  getGrowthLearning,
  getGrowthOverview,
  listGrowthActions,
  listGrowthFeedCandidates,
  listGrowthPlatformAccounts,
  listGrowthTargets,
  rejectGrowthFeedCandidate,
  runGrowthDiscoveryIntake,
  runGrowthFeedIntake,
  rewriteGrowthAction,
  retryGrowthFailedActions,
  sendGrowthAction,
  uploadGrowthContentAssets,
  syncGrowthBufferChannels,
  runGrowthAgents,
  updateGrowthPlatformAccount,
  updateGrowthTarget,
} from "../../api/growth";

const PRIMARY_TABS = [
  {
    value: "pending",
    label: "Pending Approvals",
    hint: "Drafts for content, engagement, and direct messaging",
  },
  {
    value: "ready",
    label: "Ready to Send",
    hint: "Approved posts and messages waiting to be sent",
  },
  {
    value: "targets",
    label: "Tracked Targets",
    hint: "Potential customers, influencers, and testers",
  },
  {
    value: "inbox",
    label: "Inbox",
    hint: "High-priority LinkedIn and Instagram messages",
  },
];

const PENDING_TABS = [
  { value: "content", label: "Content Generation" },
  { value: "engagement", label: "Engagement" },
  { value: "dm", label: "Direct Messaging" },
];

const emptyComposer = {
  platform: "linkedin",
  action_type: "post",
  target_id: "",
  title: "",
  campaign_key: "manual_growth_action",
  reason: "",
  draft_text: "",
  external_url: "",
  risk_flags: "owner_approval_required",
};

const emptyTargetForm = {
  platform: "linkedin",
  handle: "",
  display_name: "",
  profile_url: "",
  audience_summary: "",
  fit_score: 50,
  status: "identified",
  target_type: "influencer",
  notes: "",
  source: "manual",
};

const emptyDiscoveryForm = {
  platform: "linkedin",
  target_type: "influencer",
  entries: "",
  notes: "",
};

const emptyFeedForm = {
  platform: "linkedin",
  entries: "",
  suggested_response: "",
  campaign_key: "feed_engagement",
};

const targetStatuses = [
  "identified",
  "queued",
  "awaiting_approval",
  "sent",
  "responded",
  "interested",
  "testing_app",
  "testimonial_received",
  "closed",
];

function MiniMetric({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
    </div>
  );
}

function formatActivityTime(value) {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function AgentActivityPanel({ agents, audit }) {
  const runs = (agents?.recent_runs || []).slice(0, 3).map((row) => ({
    id: `run-${row.id}`,
    label: row.status === "running" ? "Agent run in progress" : "Agent run completed",
    detail: row.note || `Scanned ${row.scanned_targets || 0} targets`,
    time: row.finished_at || row.started_at || row.created_at,
    status: row.status || "completed",
  }));
  const events = (audit?.recent_events || [])
    .filter((row) => row.actor_email === null || row.actor_email === undefined || row.actor_email === "system")
    .slice(0, 3)
    .map((row, idx) => ({
      id: `event-${row.entity_id || idx}-${row.created_at || idx}`,
      label: `${String(row.entity_type || "agent").replaceAll("_", " ")}: ${String(row.action || "activity").replaceAll("_", " ")}`,
      detail: row.detail || row.status || "Completed",
      time: row.created_at,
      status: row.status || "ok",
    }));
  const activity = [...runs, ...events]
    .sort((left, right) => new Date(right.time || 0).getTime() - new Date(left.time || 0).getTime())
    .slice(0, 5);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Live agent activity</p>
        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          Refreshes every 10s
        </span>
      </div>
      <div className="mt-3 space-y-2">
        {activity.length ? activity.map((row) => (
          <div key={row.id} className="flex items-start justify-between gap-3 border-t border-slate-100 pt-2 first:border-t-0 first:pt-0">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium capitalize text-slate-900">{row.label}</p>
              <p className="truncate text-xs text-slate-500">{row.detail}</p>
            </div>
            <time className="shrink-0 text-xs text-slate-500">{formatActivityTime(row.time)}</time>
          </div>
        )) : <p className="text-sm text-slate-500">No agent activity recorded yet.</p>}
      </div>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
    </div>
  );
}

function PlatformBadge({ platform }) {
  return (
    <span className="inline-flex rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700">
      {String(platform || "").trim() || "unknown"}
    </span>
  );
}

function SocialLink({ href, children, className = "" }) {
  const url = String(href || "").trim();
  if (!url) return <span className={className}>{children}</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className={`${className} text-sky-700 hover:underline`}>
      {children}
    </a>
  );
}

function toApiUrl(pathOrUrl) {
  const value = String(pathOrUrl || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const base = String(API_BASE_URL || "").replace(/\/+$/, "");
  if (!base) return value;
  return `${base}${value.startsWith("/") ? value : `/${value}`}`;
}

function normalizeSocialHandle(value) {
  return String(value || "")
    .trim()
    .replace(/^@+/, "")
    .replace(/^https?:\/\/(www\.)?/i, "")
    .replace(/\/+$/, "");
}

function isFakeGrowthSource(source) {
  const value = String(source || "").trim().toLowerCase();
  return value.includes("demo") || value.startsWith("local") || value.startsWith("seed");
}

function resolveTargetSocialUrl(target) {
  if (!target) return "";
  const explicit = String(target.profile_url || target.account_url || target.social_url || target.url || "").trim();
  if (explicit) return explicit;
  const platform = String(target.platform || "").trim().toLowerCase();
  const handle = normalizeSocialHandle(target.handle);
  const label = normalizeSocialHandle(target.display_name || target.handle);
  if (platform === "instagram" && handle) {
    return `https://www.instagram.com/${handle}/`;
  }
  if (platform === "linkedin" && (handle || label)) {
    const query = encodeURIComponent(handle || label);
    return `https://www.linkedin.com/search/results/all/?keywords=${query}`;
  }
  return "";
}

function resolveInboxSenderUrl(row) {
  if (!row) return "";
  const explicit = String(row.sender_url || row.profile_url || row.account_url || row.social_url || row.thread_url || "").trim();
  if (explicit) return explicit;
  const platform = String(row.platform || "").trim().toLowerCase();
  const handle = normalizeSocialHandle(row.sender_handle);
  const label = normalizeSocialHandle(row.sender_name || row.sender_handle);
  if (platform === "instagram" && handle) {
    return `https://www.instagram.com/${handle}/`;
  }
  if (platform === "linkedin" && (handle || label)) {
    const query = encodeURIComponent(handle || label);
    return `https://www.linkedin.com/search/results/all/?keywords=${query}`;
  }
  return "";
}

function isVisibleTarget(target) {
  if (!target) return false;
  if (isFakeGrowthSource(target.source)) return false;
  return Boolean(resolveTargetSocialUrl(target));
}

function isAgentGeneratedAction(action) {
  const campaign = String(action?.campaign_key || "").trim().toLowerCase();
  return campaign.startsWith("growth_agent_");
}

function isVisibleGrowthAction(action) {
  if (!action) return false;
  const platform = String(action.platform || "").trim().toLowerCase();
  if (!["instagram", "linkedin"].includes(platform)) return false;
  const actionType = String(action.action_type || "").trim().toLowerCase();
  if (actionType === "post") return isAgentGeneratedAction(action);
  if (["dm", "follow_up", "partnership", "reply"].includes(actionType)) return isAgentGeneratedAction(action) && isVisibleTarget(action.target);
  return true;
}

function TargetSummary({ target }) {
  if (!target) return null;
  const profileUrl = resolveTargetSocialUrl(target);
  const displayName = target.display_name || target.handle || "Target";
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
      <SocialLink href={profileUrl} className="rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5">
        {displayName}
      </SocialLink>
      {target.handle ? <SocialLink href={profileUrl}>{target.handle}</SocialLink> : null}
      {target.target_type ? <span>{target.target_type}</span> : null}
      {target.fit_score !== undefined && target.fit_score !== null ? <span>Fit {Math.round(Number(target.fit_score || 0))}</span> : null}
    </div>
  );
}

function ActionMediaPreview({ action, sourceAsset }) {
  const previewUrl = toApiUrl(action?.external_url);
  const showPreview = Boolean(previewUrl && action?.action_type === "post");
  if (!showPreview) return null;

  const mediaAvailable = sourceAsset ? sourceAsset.available !== false : false;
  if (!mediaAvailable) {
    return (
      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        Source media is unavailable. Re-upload the file to restore this attachment.
      </div>
    );
  }

  const isVideo = String(sourceAsset?.content_type || "").toLowerCase().startsWith("video/");
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Attached media</p>
        <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline">Open</a>
      </div>
      {isVideo ? (
        <video src={previewUrl} controls preload="metadata" className="h-52 w-full bg-black object-contain" />
      ) : (
        <img src={previewUrl} alt={sourceAsset?.original_filename || sourceAsset?.filename || "Attached post media"} className="h-52 w-full object-cover" />
      )}
    </div>
  );
}

function ActionCard({ action, contentMode = false, mediaType = "", mediaAvailable = true, deleting = false, busyId, busyRewriteId, choice, selectedAlternativeId, onApprove, onDisapprove, onDelete, onRewrite, onSelectAlternative, onChoice }) {
  const rejected = String(action?.status || "") === "rejected";
  const showAlternatives = choice === "disapprove" || rejected;
  const isBusy = busyId === action.id || busyRewriteId === action.id;
  const previewUrl = toApiUrl(action?.external_url);
  const showPreview = Boolean(previewUrl && action.action_type === "post");
  return (
    <div className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      {contentMode ? (
        <Button
          className="absolute right-3 top-3"
          size="sm"
          variant="ghost"
          onClick={() => onDelete(action.id)}
          disabled={isBusy || deleting}
          aria-label="Delete content suggestion"
        >
          {deleting ? "Deleting..." : "Delete"}
        </Button>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className={contentMode ? "pr-24" : ""}>
          <div className="flex items-center gap-2">
            <PlatformBadge platform={action.platform} />
            <span className="text-xs uppercase tracking-wide text-slate-500">{action.action_type}</span>
            <span className={`text-xs font-medium ${action.status === "approved" ? "text-emerald-700" : action.status === "rejected" ? "text-rose-700" : "text-amber-700"}`}>
              {action.status}
            </span>
          </div>
          {!contentMode ? <h3 className="mt-2 text-base font-semibold text-slate-900">{action.title || "Proposed action"}</h3> : null}
          {!contentMode ? <TargetSummary target={action.target} /> : null}
          {!contentMode && action.reason ? <p className="mt-1 text-sm text-slate-600">{action.reason}</p> : null}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => onApprove(action.id)}
            disabled={isBusy}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onDisapprove(action.id, selectedAlternativeId)}
            disabled={isBusy}
          >
            Reject
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChoice(action.id, showAlternatives ? "" : "disapprove")}
            disabled={isBusy}
          >
            Alternatives
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onRewrite(action.id)}
            disabled={isBusy}
          >
            {busyRewriteId === action.id ? "Rewriting..." : "Rewrite"}
          </Button>
        </div>
      </div>
      {showPreview && mediaAvailable ? (
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Preview</p>
            {action.platform === "instagram" && action.action_type === "post" ? (
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700">Motion ready</span>
            ) : null}
          </div>
          {String(mediaType || "").toLowerCase().startsWith("video/") ? (
            <video src={previewUrl} controls preload="metadata" className="h-52 w-full bg-black object-contain" />
          ) : (
            <img src={previewUrl} alt="" className="h-52 w-full object-cover" />
          )}
        </div>
      ) : showPreview ? (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Source media is unavailable. Re-upload the file to restore this preview.
        </div>
      ) : null}
      {contentMode ? <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-sky-700">Suggested caption</p> : null}
      <div className={`mt-2 whitespace-pre-wrap rounded-lg p-4 text-sm leading-6 ${contentMode ? "border-2 border-sky-300 bg-sky-50 text-slate-900" : "bg-slate-50 text-slate-800"}`}>{action.draft_text}</div>
      {!contentMode && (action.risk_flags || []).length ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {action.risk_flags.map((flag) => (
            <span key={`${action.id}-${flag}`} className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
              {flag}
            </span>
          ))}
        </div>
      ) : null}
      {showAlternatives ? (
        <div className="mt-4 rounded-xl border border-slate-200 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Choose 1 of 3 alternatives</p>
          <div className="mt-3 space-y-2">
            {(action.alternatives || []).slice(0, 3).map((alt) => (
              <label key={alt.id} className={`flex cursor-pointer gap-3 rounded-lg p-3 ${contentMode ? "border-2 border-sky-200 bg-sky-50/60" : "border border-slate-200"}`}>
                <input
                  type="radio"
                  name={`alt-${action.id}`}
                  checked={selectedAlternativeId === alt.id}
                  onChange={() => onSelectAlternative(action.id, alt.id)}
                />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{alt.label}</p>
                  <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{alt.draft_text}</p>
                  <p className="mt-1 text-xs text-slate-500">{alt.rationale}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onDisapprove(action.id, selectedAlternativeId)}
              disabled={busyId === action.id}
            >
              Save Rejection
            </Button>
            <Button
              size="sm"
              onClick={() => onApprove(action.id, selectedAlternativeId)}
              disabled={busyId === action.id}
            >
              Approve Selected Option
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PlatformAccountCard({ account, edit, busyPlatform, onEdit, onSave }) {
  const platform = String(account?.platform || "");
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <PlatformBadge platform={platform} />
            <span className={`text-xs font-medium ${account.connector_mode === "disabled" ? "text-slate-500" : "text-emerald-700"}`}>
              {account.connector_mode}
            </span>
            {account.connector_mode === "live" ? (
              <span className={`text-xs font-medium ${account.live_ready ? "text-emerald-700" : "text-amber-700"}`}>
                {account.live_ready ? "live ready" : "live needs secrets"}
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-base font-semibold text-slate-900">{account.display_name || platform}</p>
          {account.connector_mode === "live" && (account.live_requirements || []).length ? (
            <p className="mt-1 text-xs text-slate-500">Needs: {account.live_requirements.join(", ")}</p>
          ) : null}
        </div>
        <Button size="sm" onClick={() => onSave(platform)} disabled={busyPlatform === platform}>
          {busyPlatform === platform ? "Saving..." : "Save"}
        </Button>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="text-sm text-slate-700">
          Account name
          <input
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
            value={edit.display_name}
            onChange={(e) => onEdit(platform, { display_name: e.target.value })}
          />
        </label>
        <label className="text-sm text-slate-700">
          Account URL
          <input
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
            value={edit.account_url}
            onChange={(e) => onEdit(platform, { account_url: e.target.value })}
          />
        </label>
        <label className="text-sm text-slate-700">
          Connector mode
          <select
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            value={edit.connector_mode}
            onChange={(e) => onEdit(platform, { connector_mode: e.target.value })}
          >
            <option value="disabled">disabled</option>
            <option value="manual">manual</option>
            <option value="live">live</option>
            <option value="buffer">buffer</option>
          </select>
        </label>
        <label className="text-sm text-slate-700">
          Buffer channel ID
          <input
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
            value={edit.buffer_channel_id}
            onChange={(e) => onEdit(platform, { buffer_channel_id: e.target.value })}
          />
        </label>
        <label className="text-sm text-slate-700">
          Notes
          <input
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
            value={edit.notes}
            onChange={(e) => onEdit(platform, { notes: e.target.value })}
          />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-700">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={Boolean(edit.can_post)} onChange={(e) => onEdit(platform, { can_post: e.target.checked })} />
          Posts
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={Boolean(edit.can_comment)} onChange={(e) => onEdit(platform, { can_comment: e.target.checked })} />
          Comments
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={Boolean(edit.can_message)} onChange={(e) => onEdit(platform, { can_message: e.target.checked })} />
          Messages
        </label>
      </div>
      {edit.connector_mode === "live" ? (
        <p className="mt-3 text-xs text-slate-500">
          Live mode uses Meta Graph API. Instagram needs `GROWTH_INSTAGRAM_ACCESS_TOKEN` and `GROWTH_INSTAGRAM_IG_USER_ID`; Facebook needs `GROWTH_FACEBOOK_PAGE_ID` and `GROWTH_FACEBOOK_PAGE_ACCESS_TOKEN`.
        </p>
      ) : null}
    </div>
  );
}

function ComposeActionForm({ form, targets, busy, onChange, onSubmit }) {
  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-sm text-slate-700">
          Platform
          <select
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            value={form.platform}
            onChange={(e) => onChange({ platform: e.target.value })}
          >
            <option value="linkedin">LinkedIn</option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
          </select>
        </label>
        <label className="text-sm text-slate-700">
          Action
          <select
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            value={form.action_type}
            onChange={(e) => onChange({ action_type: e.target.value })}
          >
            <option value="post">Post</option>
            <option value="comment">Comment</option>
            <option value="reply">Reply</option>
            <option value="dm">Direct message</option>
            <option value="follow_up">Follow-up</option>
            <option value="partnership">Partnership</option>
          </select>
        </label>
        <label className="text-sm text-slate-700">
          Campaign
          <input
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
            value={form.campaign_key}
            onChange={(e) => onChange({ campaign_key: e.target.value })}
          />
        </label>
      </div>
      <label className="block text-sm text-slate-700">
        Target
        <select
          className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
          value={form.target_id}
          onChange={(e) => onChange({ target_id: e.target.value })}
        >
          <option value="">No target attached</option>
          {(targets || []).map((target) => (
            <option key={target.id} value={target.id}>
              {(target.display_name || target.handle || "Untitled target")} ({target.platform}, fit {Math.round(Number(target.fit_score || 0))})
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm text-slate-700">
        Title
        <input
          className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
          value={form.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Short approval title"
        />
      </label>
      <label className="block text-sm text-slate-700">
        Reason
        <textarea
          className="mt-1 min-h-[80px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={form.reason}
          onChange={(e) => onChange({ reason: e.target.value })}
          placeholder="Why this action is worth approving"
        />
      </label>
      <label className="block text-sm text-slate-700">
        Draft
        <textarea
          className="mt-1 min-h-[180px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={form.draft_text}
          onChange={(e) => onChange({ draft_text: e.target.value })}
          placeholder="Write the post, comment, reply, or outreach message"
        />
      </label>
      <label className="block text-sm text-slate-700">
        External URL
        <input
          className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
          value={form.external_url}
          onChange={(e) => onChange({ external_url: e.target.value })}
          placeholder="Public image URL for Instagram live posts, or a Graph object/comment URL for replies"
        />
      </label>
      {form.platform === "instagram" && form.action_type === "post" ? (
        <p className="text-xs text-slate-500">Instagram live post mode uses this URL as the image source when the action is sent.</p>
      ) : null}
      <label className="block text-sm text-slate-700">
        Risk flags
        <input
          className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
          value={form.risk_flags}
          onChange={(e) => onChange({ risk_flags: e.target.value })}
          placeholder="Comma-separated flags"
        />
      </label>
      <div className="flex justify-end">
        <Button type="submit" disabled={busy}>
          {busy ? "Creating..." : "Create Approval"}
        </Button>
      </div>
    </form>
  );
}

function TargetForm({ form, busy, submitLabel, onChange, onSubmit, onCancel }) {
  return (
    <form className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4" onSubmit={onSubmit}>
      <div className="grid gap-3 md:grid-cols-4">
        <label className="text-sm text-slate-700">
          Platform
          <select
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            value={form.platform}
            onChange={(e) => onChange({ platform: e.target.value })}
          >
            <option value="linkedin">LinkedIn</option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
          </select>
        </label>
        <label className="text-sm text-slate-700">
          Type
          <select
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            value={form.target_type}
            onChange={(e) => onChange({ target_type: e.target.value })}
          >
            <option value="influencer">Influencer</option>
            <option value="testimonial_candidate">Testimonial candidate</option>
            <option value="community">Community</option>
            <option value="lead">Lead</option>
          </select>
        </label>
        <label className="text-sm text-slate-700">
          Status
          <select
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            value={form.status}
            onChange={(e) => onChange({ status: e.target.value })}
          >
            {targetStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
        </label>
        <label className="text-sm text-slate-700">
          Fit score
          <input
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
            type="number"
            min="0"
            max="100"
            value={form.fit_score}
            onChange={(e) => onChange({ fit_score: e.target.value })}
          />
        </label>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm text-slate-700">
          Handle
          <input
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
            value={form.handle}
            onChange={(e) => onChange({ handle: e.target.value })}
            placeholder="@profile or page name"
          />
        </label>
        <label className="text-sm text-slate-700">
          Display name
          <input
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
            value={form.display_name}
            onChange={(e) => onChange({ display_name: e.target.value })}
          />
        </label>
      </div>
      <label className="block text-sm text-slate-700">
        Profile URL
        <input
          className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
          value={form.profile_url}
          onChange={(e) => onChange({ profile_url: e.target.value })}
        />
      </label>
      <label className="block text-sm text-slate-700">
        Audience summary
        <textarea
          className="mt-1 min-h-[72px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={form.audience_summary}
          onChange={(e) => onChange({ audience_summary: e.target.value })}
        />
      </label>
      <label className="block text-sm text-slate-700">
        Notes
        <textarea
          className="mt-1 min-h-[72px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={form.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
        />
      </label>
      <div className="flex justify-end gap-2">
        {onCancel ? <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button> : null}
        <Button type="submit" disabled={busy}>{busy ? "Saving..." : submitLabel}</Button>
      </div>
    </form>
  );
}

function SendControls({ action, value, busy, onChange, onSend }) {
  const mode = value?.buffer_share_mode || "addToQueue";
  const account = action?.platform_account || {};
  const ready = Boolean(account.ready_for_action);
  const isBuffer = account.connector_mode === "buffer";
  const isLive = account.connector_mode === "live";
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span className={`rounded-full border px-2 py-0.5 ${ready ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
          {ready ? "Connector ready" : "Connector needs setup"}
        </span>
        <span>{account.connector_mode || "disabled"}</span>
        {account.required_capability ? <span>{account.required_capability}</span> : null}
        {account.buffer_channel_id ? <span>Buffer {account.buffer_channel_id}</span> : null}
      </div>
      {isBuffer ? (
        <div className="grid gap-3 md:grid-cols-[minmax(0,220px)_minmax(0,260px)_auto] md:items-end">
          <label className="text-sm text-slate-700">
            Buffer send mode
            <select
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
              value={mode}
              onChange={(e) => onChange(action.id, { buffer_share_mode: e.target.value })}
            >
              <option value="addToQueue">Add to queue</option>
              <option value="shareNext">Share next</option>
              <option value="shareNow">Share now</option>
              <option value="customScheduled">Schedule time</option>
              <option value="draft">Save draft</option>
            </select>
          </label>
          {mode === "customScheduled" ? (
            <label className="text-sm text-slate-700">
              Scheduled time
              <input
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                type="datetime-local"
                value={value?.buffer_due_at || ""}
                onChange={(e) => onChange(action.id, { buffer_due_at: e.target.value })}
              />
            </label>
          ) : <div />}
          <Button size="sm" onClick={() => onSend(action.id)} disabled={busy || !ready}>
            {busy ? "Sending..." : action.status === "failed" ? "Retry Send" : "Send Approved"}
          </Button>
        </div>
      ) : (
        <div className="flex items-end justify-between gap-3">
          <div className="text-xs text-slate-500">
            {isLive ? (
              <p>Live mode sends directly through Meta. Instagram post actions use the action&apos;s External URL as the image source.</p>
            ) : (
              <p>Manual mode will mark the action sent without a connector.</p>
            )}
            {action.external_url ? <p className="mt-1">External URL: {action.external_url}</p> : null}
          </div>
          <Button size="sm" onClick={() => onSend(action.id)} disabled={busy || !ready}>
            {busy ? "Sending..." : action.status === "failed" ? "Retry Send" : "Send Approved"}
          </Button>
        </div>
      )}
      {isBuffer ? <p className="mt-2 text-xs text-slate-500">Scheduling options apply when this platform is in Buffer connector mode.</p> : null}
      {!ready ? <p className="mt-2 text-xs text-amber-700">Update this platform in Platforms before sending.</p> : null}
    </div>
  );
}

function DiscoveryIntakeForm({ form, result, busy, onChange, onSubmit }) {
  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm text-slate-700">
          Default platform
          <select
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            value={form.platform}
            onChange={(e) => onChange({ platform: e.target.value })}
          >
            <option value="linkedin">LinkedIn</option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
          </select>
        </label>
        <label className="text-sm text-slate-700">
          Target type
          <select
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            value={form.target_type}
            onChange={(e) => onChange({ target_type: e.target.value })}
          >
            <option value="influencer">Influencer</option>
            <option value="testimonial_candidate">Testimonial candidate</option>
            <option value="community">Community</option>
            <option value="lead">Lead</option>
          </select>
        </label>
      </div>
      <label className="block text-sm text-slate-700">
        URLs or handles
        <textarea
          className="mt-1 min-h-[180px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={form.entries}
          onChange={(e) => onChange({ entries: e.target.value })}
          placeholder="One profile URL or handle per line"
        />
      </label>
      <label className="block text-sm text-slate-700">
        Notes
        <textarea
          className="mt-1 min-h-[80px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={form.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Audience context, campaign, or why these prospects matter"
        />
      </label>
      <div className="flex justify-end">
        <Button type="submit" disabled={busy}>{busy ? "Importing..." : "Import Targets"}</Button>
      </div>
      {result ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-sm font-semibold text-slate-900">
            Created {(result.targets || []).length} target(s), skipped {(result.skipped || []).length}
          </p>
          {(result.targets || []).length ? (
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {result.targets.map((target) => (
                <div key={target.id} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{target.display_name || target.handle}</p>
                    <PlatformBadge platform={target.platform} />
                  </div>
                  <p className="mt-1 text-xs text-slate-600">{target.handle}</p>
                  <p className="mt-2 text-sm text-slate-700">{target.audience_summary}</p>
                  <p className="mt-2 text-xs text-slate-500">Fit {Math.round(Number(target.fit_score || 0))}</p>
                </div>
              ))}
            </div>
          ) : null}
          {(result.skipped || []).length ? (
            <div className="mt-3 text-xs text-slate-500">
              Skipped: {result.skipped.map((row) => `${row.entry} (${row.reason})`).join(", ")}
            </div>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}

function FeedIntakeForm({ form, result, busy, onChange, onSubmit }) {
  return (
    <form className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4" onSubmit={onSubmit}>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm text-slate-700">
          Default platform
          <select
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            value={form.platform}
            onChange={(e) => onChange({ platform: e.target.value })}
          >
            <option value="linkedin">LinkedIn</option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
          </select>
        </label>
        <label className="text-sm text-slate-700">
          Campaign
          <input
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
            value={form.campaign_key}
            onChange={(e) => onChange({ campaign_key: e.target.value })}
          />
        </label>
      </div>
      <label className="block text-sm text-slate-700">
        Post URLs or post text
        <textarea
          className="mt-1 min-h-[120px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={form.entries}
          onChange={(e) => onChange({ entries: e.target.value })}
          placeholder="One feed post URL or text snippet per line"
        />
      </label>
      <label className="block text-sm text-slate-700">
        Suggested response override
        <textarea
          className="mt-1 min-h-[72px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={form.suggested_response}
          onChange={(e) => onChange({ suggested_response: e.target.value })}
          placeholder="Leave blank to use the generated engagement response"
        />
      </label>
      <div className="flex justify-end">
        <Button type="submit" disabled={busy}>{busy ? "Importing..." : "Import Feed Candidates"}</Button>
      </div>
      {result ? (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-800">
          Created {(result.feed_candidates || []).length} candidate(s) and {(result.actions || []).length} approval action(s). Skipped {(result.skipped || []).length}.
        </p>
      ) : null}
    </form>
  );
}

export default function GrowthConsole() {
  const [tab, setTab] = React.useState("");
  const [approvalTab, setApprovalTab] = React.useState("content");
  const [overview, setOverview] = React.useState(null);
  const [targets, setTargets] = React.useState([]);
  const [contentAssets, setContentAssets] = React.useState([]);
  const [actions, setActions] = React.useState([]);
  const [platformAccounts, setPlatformAccounts] = React.useState([]);
  const [platformEdits, setPlatformEdits] = React.useState({});
  const [feedCandidates, setFeedCandidates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [busyId, setBusyId] = React.useState("");
  const [busyRewriteId, setBusyRewriteId] = React.useState("");
  const [busyPlatform, setBusyPlatform] = React.useState("");
  const [bulkRetrying, setBulkRetrying] = React.useState(false);
  const [syncingBuffer, setSyncingBuffer] = React.useState(false);
  const [bufferSyncSummary, setBufferSyncSummary] = React.useState("");
  const [creatingAction, setCreatingAction] = React.useState(false);
  const [choices, setChoices] = React.useState({});
  const [selectedAlternatives, setSelectedAlternatives] = React.useState({});
  const [composer, setComposer] = React.useState(emptyComposer);
  const [targetForm, setTargetForm] = React.useState(emptyTargetForm);
  const [editingTargetId, setEditingTargetId] = React.useState("");
  const [busyTarget, setBusyTarget] = React.useState("");
  const [sendOptions, setSendOptions] = React.useState({});
  const [discoveryForm, setDiscoveryForm] = React.useState(emptyDiscoveryForm);
  const [discoveryResult, setDiscoveryResult] = React.useState(null);
  const [runningDiscovery, setRunningDiscovery] = React.useState(false);
  const [feedForm, setFeedForm] = React.useState(emptyFeedForm);
  const [feedIntakeResult, setFeedIntakeResult] = React.useState(null);
  const [runningFeedIntake, setRunningFeedIntake] = React.useState(false);
  const [learning, setLearning] = React.useState(null);
  const [audit, setAudit] = React.useState(null);
  const [agents, setAgents] = React.useState(null);
  const [inboxMessages, setInboxMessages] = React.useState([]);
  const [contentAssetFiles, setContentAssetFiles] = React.useState([]);
  const [contentAssetNotes, setContentAssetNotes] = React.useState("");
  const [uploadingContentAssets, setUploadingContentAssets] = React.useState(false);
  const [deletingContentAssetId, setDeletingContentAssetId] = React.useState("");
  const [deletingActionId, setDeletingActionId] = React.useState("");
  const [contentAssetMessage, setContentAssetMessage] = React.useState("");
  const [contentAssetInputKey, setContentAssetInputKey] = React.useState(0);
  const [expandedEngagementActions, setExpandedEngagementActions] = React.useState({});
  const [rejectingFeedCandidateId, setRejectingFeedCandidateId] = React.useState("");
  const [auditFilters, setAuditFilters] = React.useState({
    entity_type: "",
    action: "",
    platform: "",
  });
  const currentPrimaryTab = PRIMARY_TABS.find((item) => item.value === tab) || null;
  const showLanding = !tab;

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [overviewRes, targetsRes, assetsRes, actionsRes, accountsRes, feedRes, learningRes, auditRes, agentsRes, inboxRes] = await Promise.all([
        getGrowthOverview(),
        listGrowthTargets(),
        listGrowthContentAssets(),
        listGrowthActions(),
        listGrowthPlatformAccounts(),
        listGrowthFeedCandidates(),
        getGrowthLearning(),
        getGrowthAudit({
          entity_type: auditFilters.entity_type || undefined,
          action: auditFilters.action || undefined,
          platform: auditFilters.platform || undefined,
          limit: 50,
        }),
        getGrowthAgents(),
        listGrowthInboxMessages(),
      ]);
      setOverview(overviewRes?.overview || null);
      setTargets(targetsRes);
      setContentAssets(Array.isArray(assetsRes) ? assetsRes : []);
      setActions(actionsRes);
      setPlatformAccounts(accountsRes);
      setPlatformEdits((prev) => {
        const next = { ...prev };
        accountsRes.forEach((account) => {
          const platform = String(account?.platform || "");
          if (!platform || next[platform]) return;
          next[platform] = {
            display_name: String(account?.display_name || ""),
            account_url: String(account?.account_url || ""),
            connector_mode: String(account?.connector_mode || "disabled"),
            buffer_channel_id: String(account?.buffer_channel_id || ""),
            can_post: Boolean(account?.can_post),
            can_message: Boolean(account?.can_message),
            can_comment: Boolean(account?.can_comment),
            notes: String(account?.notes || ""),
          };
        });
        return next;
      });
      setFeedCandidates(feedRes);
      setLearning(learningRes?.learning || null);
      setAudit(auditRes?.audit || null);
      setAgents(agentsRes?.agents || null);
      setInboxMessages(inboxRes);
    } catch (e) {
      setError(e?.message || "Failed to load growth console");
    } finally {
      setLoading(false);
    }
  }, [auditFilters.action, auditFilters.entity_type, auditFilters.platform]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  React.useEffect(() => {
    const showsAgentActivity = tab === "targets" || tab === "inbox" || (tab === "pending" && approvalTab === "content");
    if (!showsAgentActivity) return undefined;
    const refreshActivity = async () => {
      try {
        const [agentsRes, auditRes] = await Promise.all([
          getGrowthAgents(),
          getGrowthAudit({ limit: 20 }),
        ]);
        setAgents(agentsRes?.agents || null);
        setAudit(auditRes?.audit || null);
      } catch (_error) {
        // Keep the last successful activity snapshot during transient polling failures.
      }
    };
    const timer = window.setInterval(refreshActivity, 10000);
    return () => window.clearInterval(timer);
  }, [approvalTab, tab]);

  const handleDecision = async (actionId, decision, selectedAlternativeId = "") => {
    setBusyId(actionId);
    setError("");
    try {
      await decideGrowthAction(actionId, {
        decision,
        selected_alternative_id: String(selectedAlternativeId || "").trim() || null,
      });
      setChoices((prev) => ({ ...prev, [actionId]: "" }));
      await loadAll();
    } catch (e) {
      setError(e?.message || "Failed to update action");
    } finally {
      setBusyId("");
    }
  };

  const updateSendOptions = (actionId, patch) => {
    setSendOptions((prev) => ({
      ...prev,
      [actionId]: {
        buffer_share_mode: "addToQueue",
        buffer_due_at: "",
        ...(prev[actionId] || {}),
        ...(patch || {}),
      },
    }));
  };

  const handleSend = async (actionId) => {
    setBusyId(actionId);
    setError("");
    try {
      const options = sendOptions[actionId] || {};
      const mode = options.buffer_share_mode || "addToQueue";
      const dueAt = options.buffer_due_at ? new Date(options.buffer_due_at).toISOString() : null;
      await sendGrowthAction(actionId, {
        buffer_share_mode: mode,
        buffer_due_at: mode === "customScheduled" ? dueAt : null,
      });
      await loadAll();
    } catch (e) {
      setError(e?.message || "Failed to send action");
      await loadAll();
    } finally {
      setBusyId("");
    }
  };

  const handleRetryFailed = async () => {
    const failedActions = actions.filter((row) => String(row?.status || "") === "failed");
    if (!failedActions.length) return;
    setBulkRetrying(true);
    setError("");
    try {
      await retryGrowthFailedActions({});
      await loadAll();
    } catch (e) {
      setError(e?.message || "Failed to retry failed actions");
      await loadAll();
    } finally {
      setBulkRetrying(false);
    }
  };

  const handleRunAgents = async () => {
    setError("");
    try {
      await runGrowthAgents();
      await loadAll();
    } catch (e) {
      setError(e?.message || "Failed to run Growth agents");
    }
  };

  const updateAuditFilters = (patch) => {
    setAuditFilters((prev) => ({ ...prev, ...(patch || {}) }));
  };

  const handleRejectFeedCandidate = async (feedCandidateId) => {
    const id = String(feedCandidateId || "").trim();
    if (!id) return;
    setRejectingFeedCandidateId(id);
    setError("");
    try {
      await rejectGrowthFeedCandidate(id, "Rejected entire engagement suggestion");
      await loadAll();
    } catch (e) {
      setError(e?.message || "Failed to reject engagement suggestion");
    } finally {
      setRejectingFeedCandidateId("");
    }
  };

  const handleRewrite = async (actionId) => {
    setBusyRewriteId(actionId);
    setError("");
    try {
      await rewriteGrowthAction(actionId, {});
      await loadAll();
    } catch (e) {
      setError(e?.message || "Failed to rewrite action");
    } finally {
      setBusyRewriteId("");
    }
  };

  const updatePlatformEdit = (platform, patch) => {
    setPlatformEdits((prev) => ({
      ...prev,
      [platform]: { ...(prev[platform] || {}), ...(patch || {}) },
    }));
  };

  const savePlatformAccount = async (platform) => {
    const key = String(platform || "");
    if (!key) return;
    setBusyPlatform(key);
    setError("");
    try {
      await updateGrowthPlatformAccount(key, platformEdits[key] || {});
      await loadAll();
    } catch (e) {
      setError(e?.message || "Failed to save platform account");
    } finally {
      setBusyPlatform("");
    }
  };

  const handleSyncBufferChannels = async () => {
    setSyncingBuffer(true);
    setError("");
    setBufferSyncSummary("");
    try {
      const res = await syncGrowthBufferChannels();
      const mapped = Array.isArray(res?.mapped) ? res.mapped : [];
      const channels = Array.isArray(res?.channels) ? res.channels : [];
      const names = mapped.map((row) => `${row.platform}: ${row.channel?.display_name || row.channel?.name || row.channel?.id}`).join(", ");
      setBufferSyncSummary(names ? `Mapped ${names}` : `No matching Growth Console platforms found in ${channels.length} Buffer channel(s).`);
      await loadAll();
    } catch (e) {
      setError(e?.message || "Failed to sync Buffer channels");
    } finally {
      setSyncingBuffer(false);
    }
  };

  const updateComposer = (patch) => {
    setComposer((prev) => ({ ...prev, ...(patch || {}) }));
  };

  const handleCreateAction = async (e) => {
    e.preventDefault();
    setCreatingAction(true);
    setError("");
    try {
      const riskFlags = String(composer.risk_flags || "")
        .split(",")
        .map((flag) => flag.trim())
        .filter(Boolean);
      await createGrowthAction({
        platform: composer.platform,
        action_type: composer.action_type,
        target_id: String(composer.target_id || "").trim() || null,
        title: composer.title,
        campaign_key: composer.campaign_key,
        reason: composer.reason,
        draft_text: composer.draft_text,
        external_url: String(composer.external_url || "").trim() || null,
        risk_flags: riskFlags,
      });
      setComposer(emptyComposer);
      setTab("pending");
      setApprovalTab("content");
      setContentTab(String(composer.platform || "instagram"));
      await loadAll();
    } catch (err) {
      setError(err?.message || "Failed to create growth action");
    } finally {
      setCreatingAction(false);
    }
  };

  const updateTargetForm = (patch) => {
    setTargetForm((prev) => ({ ...prev, ...(patch || {}) }));
  };

  const updateDiscoveryForm = (patch) => {
    setDiscoveryForm((prev) => ({ ...prev, ...(patch || {}) }));
  };

  const handleDiscoveryIntake = async (e) => {
    e.preventDefault();
    setRunningDiscovery(true);
    setError("");
    try {
      const result = await runGrowthDiscoveryIntake(discoveryForm);
      setDiscoveryResult(result);
      setDiscoveryForm((prev) => ({ ...prev, entries: "" }));
      await loadAll();
    } catch (err) {
      setError(err?.message || "Failed to import discovery targets");
    } finally {
      setRunningDiscovery(false);
    }
  };

  const updateFeedForm = (patch) => {
    setFeedForm((prev) => ({ ...prev, ...(patch || {}) }));
  };

  const handleFeedIntake = async (e) => {
    e.preventDefault();
    setRunningFeedIntake(true);
    setError("");
    try {
      const result = await runGrowthFeedIntake(feedForm);
      setFeedIntakeResult(result);
      setFeedForm((prev) => ({ ...prev, entries: "" }));
      await loadAll();
    } catch (err) {
      setError(err?.message || "Failed to import feed candidates");
    } finally {
      setRunningFeedIntake(false);
    }
  };

  const handleUploadContentAssets = async (e) => {
    e.preventDefault();
    setError("");
    setContentAssetMessage("");
    const files = Array.from(contentAssetFiles || []).filter(Boolean);
    if (!files.length) {
      setError("Choose at least one image or video to upload.");
      return;
    }
    setUploadingContentAssets(true);
    try {
      const uploadedGroups = [];
      for (const file of files) {
        uploadedGroups.push(await uploadGrowthContentAssets({
          files: [file],
          notes: contentAssetNotes,
        }));
      }
      const assets = uploadedGroups.flat();
      setContentAssetMessage(`Uploaded ${assets.length} asset${assets.length === 1 ? "" : "s"} and started content suggestion generation.`);
      setContentAssetFiles([]);
      setContentAssetNotes("");
      setContentAssetInputKey((prev) => prev + 1);
      await loadAll();
    } catch (err) {
      setError(err?.message || "Failed to upload content assets");
    } finally {
      setUploadingContentAssets(false);
    }
  };

  const handleDeleteContentAsset = async (assetId) => {
    const id = String(assetId || "").trim();
    if (!id) return;
    setDeletingContentAssetId(id);
    setError("");
    setContentAssetMessage("");
    try {
      await deleteGrowthContentAsset(id);
      setContentAssetMessage("Removed uploaded source asset.");
      await loadAll();
    } catch (err) {
      setError(err?.message || "Failed to remove content asset");
    } finally {
      setDeletingContentAssetId("");
    }
  };

  const handleDeleteAction = async (actionId) => {
    const id = String(actionId || "").trim();
    if (!id) return;
    setDeletingActionId(id);
    setError("");
    try {
      await deleteGrowthAction(id);
      await loadAll();
    } catch (err) {
      setError(err?.message || "Failed to delete content suggestion");
    } finally {
      setDeletingActionId("");
    }
  };

  const targetPayloadFromForm = (form) => ({
    platform: form.platform,
    handle: form.handle,
    display_name: form.display_name,
    profile_url: form.profile_url,
    audience_summary: form.audience_summary,
    fit_score: Number(form.fit_score || 0),
    status: form.status,
    target_type: form.target_type,
    notes: form.notes,
    source: form.source || "manual",
  });

  const startEditTarget = (target) => {
    setEditingTargetId(String(target?.id || ""));
    setTargetForm({
      platform: String(target?.platform || "linkedin"),
      handle: String(target?.handle || ""),
      display_name: String(target?.display_name || ""),
      profile_url: String(target?.profile_url || ""),
      audience_summary: String(target?.audience_summary || ""),
      fit_score: Number(target?.fit_score || 0),
      status: String(target?.status || "identified"),
      target_type: String(target?.target_type || "influencer"),
      notes: String(target?.notes || ""),
      source: String(target?.source || "manual"),
    });
  };

  const resetTargetForm = () => {
    setEditingTargetId("");
    setTargetForm(emptyTargetForm);
  };

  const handleSaveTarget = async (e) => {
    e.preventDefault();
    const busyKey = editingTargetId || "new";
    setBusyTarget(busyKey);
    setError("");
    try {
      if (editingTargetId) {
        await updateGrowthTarget(editingTargetId, targetPayloadFromForm(targetForm));
      } else {
        await createGrowthTarget(targetPayloadFromForm(targetForm));
      }
      resetTargetForm();
      await loadAll();
    } catch (err) {
      setError(err?.message || "Failed to save target");
    } finally {
      setBusyTarget("");
    }
  };

  const pendingActions = actions.filter((row) => String(row?.status || "") === "proposed").filter((row) => isVisibleGrowthAction(row));
  const readyActions = actions.filter((row) => ["approved", "failed"].includes(String(row?.status || ""))).filter((row) => isVisibleGrowthAction(row));
  const historicalActions = actions
    .filter((row) => !["proposed", "approved", "failed"].includes(String(row?.status || "")))
    .filter((row) => isVisibleGrowthAction(row));
  const visibleTargets = targets.filter((target) => isVisibleTarget(target));
  const contentDrafts = actions
    .filter((row) => String(row?.action_type || "") === "post")
    .filter((row) => ["instagram", "linkedin"].includes(String(row?.platform || "")))
    .filter((row) => isAgentGeneratedAction(row))
    .filter((row) => String(row?.campaign_key || "").toLowerCase() === "growth_agent_content_upload")
    .slice()
    .sort((left, right) => {
      const rank = { proposed: 0, approved: 1, failed: 2, rejected: 3 };
      return (rank[String(left?.status || "")] ?? 99) - (rank[String(right?.status || "")] ?? 99);
    });
  const targetEngagementDrafts = actions
    .filter((row) => String(row?.action_type || "") === "post")
    .filter((row) => ["instagram", "linkedin"].includes(String(row?.platform || "")))
    .filter((row) => isAgentGeneratedAction(row))
    .filter((row) => String(row?.campaign_key || "").toLowerCase() !== "growth_agent_content_upload")
    .slice()
    .sort((left, right) => {
      const rank = { proposed: 0, approved: 1, failed: 2, rejected: 3 };
      return (rank[String(left?.status || "")] ?? 99) - (rank[String(right?.status || "")] ?? 99);
    });
  const dmDrafts = actions
    .filter((row) => ["dm", "follow_up", "partnership", "reply"].includes(String(row?.action_type || "")))
    .filter((row) => ["instagram", "linkedin"].includes(String(row?.platform || "")))
    .filter((row) => isAgentGeneratedAction(row))
    .filter((row) => isVisibleTarget(row?.target))
    .slice()
    .sort((left, right) => {
      const rank = { proposed: 0, approved: 1, failed: 2, rejected: 3 };
      return (rank[String(left?.status || "")] ?? 99) - (rank[String(right?.status || "")] ?? 99);
    });
  const approvedContentAction = contentDrafts.find((row) => String(row?.status || "") === "approved") || null;
  const engagementRows = [...feedCandidates]
    .filter((row) => String(row?.status || "recommended").toLowerCase() !== "rejected")
    .sort((left, right) => Number(right?.priority_score || 0) - Number(left?.priority_score || 0));
  const inboxRows = [...inboxMessages]
    .filter((row) => ["linkedin", "instagram"].includes(String(row?.platform || "").toLowerCase()))
    .sort((left, right) => Number(right?.priority_score || 0) - Number(left?.priority_score || 0));
  const targetSections = [
    {
      key: "customers",
      title: "Potential Customers",
      items: visibleTargets.filter((target) => ["community", "lead", "prospect"].includes(String(target?.target_type || "")) || !["influencer", "testimonial_candidate", "community", "lead", "prospect"].includes(String(target?.target_type || ""))),
    },
    {
      key: "influencers",
      title: "Influencers",
      items: visibleTargets.filter((target) => String(target?.target_type || "") === "influencer"),
    },
    {
      key: "testers",
      title: "Potential Testers",
      items: visibleTargets.filter((target) => String(target?.target_type || "") === "testimonial_candidate"),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {showLanding ? (
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Growth Console</h1>
            <p className="mt-1 text-sm text-slate-500">Choose a work area to open a focused view.</p>
          </div>
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p> : null}
          <div className="grid gap-3 md:grid-cols-3">
            {PRIMARY_TABS.map((value) => (
              <Button
                key={value.value}
                variant="secondary"
                onClick={() => setTab(value.value)}
                className="min-h-28 w-full flex-col items-start justify-between gap-2 px-4 py-5 text-left"
              >
                <span className="text-lg font-semibold">{value.label}</span>
                <span className="text-sm font-normal leading-5 text-slate-500">{value.hint}</span>
              </Button>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Button size="sm" variant="ghost" onClick={() => setTab("")}>Back to Growth Console</Button>
              <h1 className="mt-2 text-2xl font-semibold text-slate-900">{currentPrimaryTab?.label || "Growth Console"}</h1>
              <p className="mt-1 text-sm text-slate-500">{currentPrimaryTab?.hint || "Choose a section."}</p>
            </div>
          </div>
        </div>
      )}

      {loading ? <p className="text-sm text-slate-500">Loading growth workspace...</p> : null}

      {!loading && tab === "pending" ? (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {PENDING_TABS.map((value) => (
              <Button
                key={value.value}
                variant={approvalTab === value.value ? "primary" : "secondary"}
                onClick={() => setApprovalTab(value.value)}
                className="px-4 py-3 text-sm"
              >
                {value.label}
              </Button>
            ))}
          </div>

          {approvalTab === "content" ? (
            <div className="space-y-6">
              <AgentActivityPanel agents={agents} audit={audit} />
              <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h2 className="text-lg font-semibold text-slate-900">Content suggestions</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Review agent-generated LinkedIn and Instagram post drafts, preview any attached media, and approve the version you want to queue or schedule.
                    </p>
                  </div>
                  {contentDrafts.length ? contentDrafts.map((action) => (
                    <div key={action.id} className="space-y-3">
                      {(() => {
                        const sourceAsset = contentAssets.find((asset) => asset.file_url === action.external_url);
                        return (
                      <ActionCard
                        action={action}
                        contentMode
                        mediaType={sourceAsset?.content_type || ""}
                        mediaAvailable={sourceAsset ? sourceAsset.available !== false : false}
                        deleting={deletingActionId === action.id}
                        busyId={busyId}
                        busyRewriteId={busyRewriteId}
                        choice={choices[action.id] || ""}
                        selectedAlternativeId={selectedAlternatives[action.id] || ""}
                        onApprove={(actionId, altId) => handleDecision(actionId, "approve", altId)}
                        onDisapprove={(actionId, altId) => handleDecision(actionId, "disapprove", altId)}
                        onDelete={handleDeleteAction}
                        onRewrite={handleRewrite}
                        onSelectAlternative={(actionId, altId) => setSelectedAlternatives((prev) => ({ ...prev, [actionId]: altId }))}
                        onChoice={(actionId, value) => setChoices((prev) => ({ ...prev, [actionId]: value }))}
                      />
                        );
                      })()}
                      {action.status === "approved" ? (
                        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                          <SendControls
                            action={action}
                            value={sendOptions[action.id] || { buffer_share_mode: "addToQueue", buffer_due_at: "" }}
                            busy={busyId === action.id}
                            onChange={updateSendOptions}
                            onSend={handleSendAction}
                          />
                        </div>
                      ) : null}
                    </div>
                  )) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
                      No post drafts yet. Run the agent or upload source media to shape the next draft.
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h3 className="text-base font-semibold text-slate-900">Source media for agent drafts</h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Upload images or videos the content agent can use when generating post suggestions.
                    </p>
                    <form className="mt-4 space-y-3" onSubmit={handleUploadContentAssets}>
                      <label className="block text-sm text-slate-700">
                        Notes
                        <textarea
                          className="mt-1 min-h-[72px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          value={contentAssetNotes}
                          onChange={(e) => setContentAssetNotes(e.target.value)}
                          placeholder="Context, angle, campaign notes, or what the agent should look for"
                        />
                      </label>
                      <label className="block text-sm text-slate-700">
                        Media files
                        <input
                          key={contentAssetInputKey}
                          className="mt-1 block w-full rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm"
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          onChange={(e) => setContentAssetFiles(Array.from(e.target.files || []))}
                        />
                      </label>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-slate-500">
                          Accepted: JPG, PNG, WEBP, GIF, MP4, MOV, M4V, WEBM
                        </p>
                        <Button type="submit" disabled={uploadingContentAssets}>
                          {uploadingContentAssets ? "Uploading..." : "Upload assets"}
                        </Button>
                      </div>
                    </form>
                    {contentAssetMessage ? <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-800">{contentAssetMessage}</p> : null}
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h3 className="text-base font-semibold text-slate-900">Uploaded source assets</h3>
                    <p className="mt-1 text-sm text-slate-500">These are saved and available to the agent when it generates content suggestions.</p>
                    <div className="mt-4 space-y-3">
                      {contentAssets.length ? contentAssets.slice(0, 6).map((asset) => {
                        const fileUrl = toApiUrl(asset.file_url);
                        const contentType = String(asset.content_type || "").toLowerCase();
                        const isImage = contentType.startsWith("image/");
                        const isVideo = contentType.startsWith("video/");
                        const isAvailable = asset.available !== false;
                        return (
                          <div key={asset.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">{asset.original_filename || asset.filename}</p>
                                <p className="text-xs text-slate-500">
                                  {asset.notes || "Shared source media"}
                                </p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                {fileUrl && isAvailable ? (
                                  <a href={fileUrl} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline">
                                    Open
                                  </a>
                                ) : null}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteContentAsset(asset.id)}
                                  disabled={deletingContentAssetId === asset.id}
                                >
                                  {deletingContentAssetId === asset.id ? "Removing..." : "Remove"}
                                </Button>
                              </div>
                            </div>
                            {fileUrl && isAvailable && isImage ? <img src={fileUrl} alt={asset.original_filename || asset.filename} className="mt-3 h-40 w-full rounded-lg object-cover" /> : null}
                            {fileUrl && isAvailable && isVideo ? <video src={fileUrl} controls preload="metadata" className="mt-3 h-40 w-full rounded-lg bg-black object-contain" /> : null}
                            {!isAvailable ? (
                              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                                File missing from legacy storage. Re-upload this source media.
                              </p>
                            ) : null}
                          </div>
                        );
                      }) : <p className="text-sm text-slate-500">No source media uploaded yet.</p>}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h3 className="text-base font-semibold text-slate-900">Scheduler</h3>
                    <p className="mt-1 text-sm text-slate-500">Approved posts can be queued, sent next, sent now, or scheduled for a specific time.</p>
                    {approvedContentAction ? (
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                        <SendControls
                          action={approvedContentAction}
                          value={sendOptions[approvedContentAction.id] || { buffer_share_mode: "addToQueue", buffer_due_at: "" }}
                          busy={busyId === approvedContentAction.id}
                          onChange={updateSendOptions}
                          onSend={handleSendAction}
                        />
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-slate-500">Approve a post draft to expose the scheduler here.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : approvalTab === "engagement" ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Engagement</h2>
                <p className="mt-1 text-sm text-slate-500">Posts the agent flagged for interaction with current connections, targets, and public feed opportunities.</p>
              </div>
              {targetEngagementDrafts.length ? targetEngagementDrafts.map((action) => (
                <div key={action.id} className="space-y-3">
                  <ActionCard
                    action={action}
                    busyId={busyId}
                    busyRewriteId={busyRewriteId}
                    choice={choices[action.id] || ""}
                    selectedAlternativeId={selectedAlternatives[action.id] || ""}
                    onApprove={(actionId, altId) => handleDecision(actionId, "approve", altId)}
                    onDisapprove={(actionId, altId) => handleDecision(actionId, "disapprove", altId)}
                    onRewrite={handleRewrite}
                    onSelectAlternative={(actionId, altId) => setSelectedAlternatives((prev) => ({ ...prev, [actionId]: altId }))}
                    onChoice={(actionId, value) => setChoices((prev) => ({ ...prev, [actionId]: value }))}
                  />
                  {action.status === "approved" ? (
                    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                      <SendControls
                        action={action}
                        value={sendOptions[action.id] || { buffer_share_mode: "addToQueue", buffer_due_at: "" }}
                        busy={busyId === action.id}
                        onChange={updateSendOptions}
                        onSend={handleSendAction}
                      />
                    </div>
                  ) : null}
                </div>
              )) : null}
              {engagementRows.length ? engagementRows.map((row) => {
                const linkedAction = row.action_id ? actions.find((action) => action.id === row.action_id) : null;
                const platformName = String(row.platform || "").toLowerCase() === "linkedin" ? "LinkedIn" : "Instagram";
                return (
                  <div key={row.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <PlatformBadge platform={row.platform} />
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                            Priority {Math.round(Number(row.priority_score || 0))}
                          </span>
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">
                            {row.triage_status || "recommended"}
                          </span>
                        </div>
                        <p className="mt-3 text-sm font-medium text-slate-900">
                          <SocialLink href={row.post_url || row.thread_url}>
                            {row.author_name || row.author_handle}
                          </SocialLink>
                        </p>
                        {row.post_url ? (
                          <a href={row.post_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-sky-700 hover:underline">
                            Open post
                          </a>
                        ) : null}
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <p>Visibility {Math.round(Number(row.visibility_score || 0))}</p>
                        <p>Feature fit {Math.round(Number(row.feature_score || 0))}</p>
                      </div>
                    </div>
                    <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-800">{row.post_text}</p>
                    {row.priority_reason ? <p className="mt-2 text-sm text-slate-700"><span className="font-semibold">Why engage:</span> {row.priority_reason}</p> : null}
                    <p className="mt-2 text-sm text-slate-700"><span className="font-semibold">Suggested response:</span> {row.suggested_response}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a
                        href={row.post_url || row.thread_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-9 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Like on {platformName}
                      </a>
                      <a
                        href={row.post_url || row.thread_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-9 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Save on {platformName}
                      </a>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => row.action_id && handleDecision(row.action_id, "approve")}>Approve response</Button>
                      <Button size="sm" variant="secondary" onClick={() => row.action_id && handleDecision(row.action_id, "disapprove")}>Reject response</Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={rejectingFeedCandidateId === row.id}
                        onClick={() => handleRejectFeedCandidate(row.id)}
                      >
                        {rejectingFeedCandidateId === row.id ? "Rejecting..." : "Reject suggestion"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (!row.action_id) return;
                          setExpandedEngagementActions((prev) => ({ ...prev, [row.action_id]: !prev[row.action_id] }));
                        }}
                      >
                        Review alternatives
                      </Button>
                    </div>
                    {linkedAction && expandedEngagementActions[linkedAction.id] ? (
                      <div className="mt-4">
                        <ActionCard
                          action={linkedAction}
                          busyId={busyId}
                          busyRewriteId={busyRewriteId}
                          choice={choices[linkedAction.id] || "disapprove"}
                          selectedAlternativeId={selectedAlternatives[linkedAction.id] || ""}
                          onApprove={(actionId, altId) => handleDecision(actionId, "approve", altId)}
                          onDisapprove={(actionId, altId) => handleDecision(actionId, "disapprove", altId)}
                          onRewrite={handleRewrite}
                          onSelectAlternative={(actionId, altId) => setSelectedAlternatives((prev) => ({ ...prev, [actionId]: altId }))}
                          onChoice={(actionId, value) => setChoices((prev) => ({ ...prev, [actionId]: value }))}
                        />
                      </div>
                    ) : null}
                    {linkedAction && String(linkedAction.status || "") === "approved" ? (
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                        <SendControls
                          action={linkedAction}
                          value={sendOptions[linkedAction.id] || { buffer_share_mode: "addToQueue", buffer_due_at: "" }}
                          busy={busyId === linkedAction.id}
                          onChange={updateSendOptions}
                          onSend={handleSendAction}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              }) : null}
              {!targetEngagementDrafts.length && !engagementRows.length ? <p className="text-sm text-slate-500">No engagement candidates yet.</p> : null}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Direct Messaging</h2>
                <p className="mt-1 text-sm text-slate-500">Suggested messages for tracked targets and potential testers, ready for approval.</p>
              </div>
              {dmDrafts.length ? dmDrafts.map((action) => (
                <div key={action.id} className="space-y-3">
                  <ActionCard
                    action={action}
                    busyId={busyId}
                    busyRewriteId={busyRewriteId}
                    choice={choices[action.id] || ""}
                    selectedAlternativeId={selectedAlternatives[action.id] || ""}
                    onApprove={(actionId, altId) => handleDecision(actionId, "approve", altId)}
                    onDisapprove={(actionId, altId) => handleDecision(actionId, "disapprove", altId)}
                    onRewrite={handleRewrite}
                    onSelectAlternative={(actionId, altId) => setSelectedAlternatives((prev) => ({ ...prev, [actionId]: altId }))}
                    onChoice={(actionId, value) => setChoices((prev) => ({ ...prev, [actionId]: value }))}
                  />
                  {action.status === "approved" ? (
                    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                      <SendControls
                        action={action}
                        value={sendOptions[action.id] || { buffer_share_mode: "addToQueue", buffer_due_at: "" }}
                        busy={busyId === action.id}
                        onChange={updateSendOptions}
                        onSend={handleSendAction}
                      />
                    </div>
                  ) : null}
                </div>
              )) : <p className="text-sm text-slate-500">No direct messaging drafts yet.</p>}
            </div>
          )}
        </div>
      ) : null}

      {!loading && tab === "ready" ? (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Ready to Send</h2>
                <p className="mt-1 text-sm text-slate-500">Approved and failed actions that can be sent or retried after connector setup is complete.</p>
              </div>
              <Button size="sm" variant="secondary" onClick={handleRetryFailed} disabled={bulkRetrying || !actions.some((row) => String(row?.status || "") === "failed")}>
                {bulkRetrying ? "Retrying..." : "Retry Failed"}
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            {readyActions.length ? readyActions.map((action) => {
              const sourceAsset = contentAssets.find((asset) => asset.file_url === action.external_url);
              return (
              <div key={action.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <PlatformBadge platform={action.platform} />
                  <span className="text-xs uppercase tracking-wide text-slate-500">{action.action_type}</span>
                  <span className={`text-xs font-medium ${action.status === "failed" ? "text-rose-700" : "text-emerald-700"}`}>{action.status}</span>
                </div>
                <p className="mt-2 text-base font-semibold text-slate-900">{action.title}</p>
                <TargetSummary target={action.target} />
                <ActionMediaPreview action={action} sourceAsset={sourceAsset} />
                <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{action.draft_text}</p>
                {action.send_error ? <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">Last send error: {action.send_error}</p> : null}
                <div className="mt-3 flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => handleRewrite(action.id)} disabled={busyId === action.id || busyRewriteId === action.id}>
                    {busyRewriteId === action.id ? "Rewriting..." : "Rewrite"}
                  </Button>
                </div>
                <SendControls
                  action={action}
                  value={sendOptions[action.id] || { buffer_share_mode: "addToQueue", buffer_due_at: "" }}
                  busy={busyId === action.id}
                  onChange={updateSendOptions}
                  onSend={handleSend}
                />
                {action.approved_at ? <p className="mt-2 text-xs text-slate-500">Approved at {action.approved_at}</p> : null}
              </div>
              );
            }) : <p className="text-sm text-slate-500">No approved actions are waiting to send.</p>}
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "discovery" ? (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-slate-900">Discovery Intake</h2>
            <p className="mt-1 text-sm text-slate-500">Paste profile URLs or handles to create scored target records for review and outreach.</p>
          </CardHeader>
          <CardBody>
            <DiscoveryIntakeForm
              form={discoveryForm}
              result={discoveryResult}
              busy={runningDiscovery}
              onChange={updateDiscoveryForm}
              onSubmit={handleDiscoveryIntake}
            />
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "targets" ? (
        <div className="space-y-6">
          <AgentActivityPanel agents={agents} audit={audit} />
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Tracked Targets</h2>
            <p className="mt-1 text-sm text-slate-500">Potential customers, influencers, and testers with links to their public profiles for review.</p>
          </div>
          <TargetForm
            form={targetForm}
            busy={busyTarget === (editingTargetId || "new")}
            submitLabel={editingTargetId ? "Save Target" : "Add Target"}
            onChange={updateTargetForm}
            onSubmit={handleSaveTarget}
            onCancel={editingTargetId ? resetTargetForm : null}
          />
          <div className="grid gap-6 xl:grid-cols-3">
            {targetSections.map((section) => (
              <div key={section.key} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">{section.items.length}</span>
                </div>
                <div className="mt-4 space-y-3">
                  {section.items.length ? section.items.map((target) => (
                    <div key={target.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-base font-semibold text-slate-900">
                            <SocialLink href={resolveTargetSocialUrl(target)}>
                              {target.display_name || target.handle || "Untitled target"}
                            </SocialLink>
                          </p>
                          <p className="text-sm text-slate-600">
                            <SocialLink href={resolveTargetSocialUrl(target)}>
                              {target.handle || "No handle yet"}
                            </SocialLink>
                          </p>
                        </div>
                        <PlatformBadge platform={target.platform} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                        <span className="rounded-full border border-slate-300 bg-white px-2 py-0.5">Fit {Math.round(Number(target.fit_score || 0))}</span>
                        <span className="rounded-full border border-slate-300 bg-white px-2 py-0.5">{target.status}</span>
                        <span className="rounded-full border border-slate-300 bg-white px-2 py-0.5">{target.target_type}</span>
                        {target.source ? <span className="rounded-full border border-slate-300 bg-white px-2 py-0.5">{target.source}</span> : null}
                      </div>
                      {target.audience_summary ? <p className="mt-3 text-sm text-slate-700">{target.audience_summary}</p> : null}
                      {target.profile_url ? (
                        <a href={target.profile_url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm text-sky-700 hover:underline">
                          Review social profile
                        </a>
                      ) : null}
                      {target.notes ? <p className="mt-3 text-sm text-slate-600">{target.notes}</p> : null}
                      <div className="mt-3 flex justify-end">
                        <Button size="sm" variant="secondary" onClick={() => startEditTarget(target)}>
                          Edit
                        </Button>
                      </div>
                    </div>
                  )) : <p className="text-sm text-slate-500">No targets in this group yet.</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {!loading && tab === "engage" ? (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-slate-900">Recommended Feed Engagement</h2>
            <p className="mt-1 text-sm text-slate-500">Import posts worth engaging with and route public responses through approvals.</p>
          </CardHeader>
          <CardBody className="space-y-3">
            <FeedIntakeForm
              form={feedForm}
              result={feedIntakeResult}
              busy={runningFeedIntake}
              onChange={updateFeedForm}
              onSubmit={handleFeedIntake}
            />
            {feedCandidates.length ? feedCandidates.map((row) => (
              <div key={row.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-base font-semibold text-slate-900">{row.author_name || row.author_handle}</p>
                    <p className="text-sm text-slate-600">{row.author_handle}</p>
                  </div>
                  <PlatformBadge platform={row.platform} />
                </div>
                <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-800">{row.post_text}</p>
                <p className="mt-3 text-sm text-slate-700"><span className="font-semibold">Why engage:</span> {row.engagement_reason}</p>
                <p className="mt-2 text-sm text-slate-700"><span className="font-semibold">Suggested response:</span> {row.suggested_response}</p>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => row.action_id && handleDecision(row.action_id, "approve")}>Approve response</Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      if (!row.action_id) return;
                      setTab("pending");
                      setApprovalTab("engagement");
                    }}
                  >
                    Review alternatives
                  </Button>
                </div>
              </div>
            )) : <p className="text-sm text-slate-500">No recommended engagements right now.</p>}
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "platforms" ? (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Platform Accounts</h2>
                <p className="mt-1 text-sm text-slate-500">Sync Buffer channels or manually configure connector settings per platform.</p>
              </div>
              <Button size="sm" variant="secondary" onClick={handleSyncBufferChannels} disabled={syncingBuffer}>
                {syncingBuffer ? "Syncing..." : "Sync Buffer Channels"}
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            {bufferSyncSummary ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-800">{bufferSyncSummary}</p> : null}
            {platformAccounts.map((account) => (
              <PlatformAccountCard
                key={account.platform}
                account={account}
                edit={platformEdits[account.platform] || {
                  display_name: "",
                  account_url: "",
                  connector_mode: "disabled",
                  buffer_channel_id: "",
                  can_post: false,
                  can_message: false,
                  can_comment: false,
                  notes: "",
                }}
                busyPlatform={busyPlatform}
                onEdit={updatePlatformEdit}
                onSave={savePlatformAccount}
              />
            ))}
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "agents" ? (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Active Agents</h2>
                <p className="mt-1 text-sm text-slate-500">Background content and outreach cycles that create approved-ready drafts.</p>
              </div>
              <Button size="sm" variant="secondary" onClick={handleRunAgents}>
                Run Now
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <MiniMetric label="Enabled" value={agents?.enabled ? "Yes" : "No"} hint="Background loop status." />
              <MiniMetric label="Interval" value={`${agents?.interval_seconds ?? 0}s`} hint="How often the loop runs." />
              <MiniMetric label="Recent runs" value={(agents?.recent_runs || []).length ?? 0} hint="Stored agent run records." />
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active roles</p>
              {(agents?.active_agents || []).length ? agents.active_agents.map((agent) => (
                <div key={agent.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{agent.id}</span>
                    <span className="text-xs uppercase tracking-wide text-slate-500">{agent.source}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-700">{agent.role}</p>
                </div>
              )) : <p className="text-sm text-slate-500">No active roles configured.</p>}
            </div>
            <div className="space-y-3">
              {(agents?.recent_runs || []).length ? agents.recent_runs.map((row) => (
                <div key={row.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{row.run_type}</span>
                    <span className="text-xs uppercase tracking-wide text-slate-500">{row.status}</span>
                    <span className="text-xs text-slate-500">{row.created_at}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-700">{row.note}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Scanned {row.scanned_targets} targets, created {row.created_actions} actions
                  </p>
                </div>
              )) : <p className="text-sm text-slate-500">No agent runs recorded yet.</p>}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "inbox" ? (
        <div className="space-y-6">
          <AgentActivityPanel agents={agents} audit={audit} />
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Inbox</h2>
            <p className="mt-1 text-sm text-slate-500">High-priority LinkedIn and Instagram messages that need a reply or review.</p>
          </div>
          <div className="space-y-4">
            {(inboxRows || []).length ? inboxRows.map((row) => {
              const linkedAction = row.action_id ? actions.find((action) => action.id === row.action_id) : null;
              return (
                <div key={row.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900">
                          <SocialLink href={resolveInboxSenderUrl(row)}>
                            {row.sender_name || row.sender_handle || row.source}
                          </SocialLink>
                        </span>
                        <PlatformBadge platform={row.platform} />
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                          Priority {Math.round(Number(row.priority_score || 0))}
                        </span>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">{row.status}</span>
                      </div>
                      {row.subject ? <p className="mt-2 text-sm font-medium text-slate-900">{row.subject}</p> : null}
                      <p className="mt-1 text-sm text-slate-600">{row.message_text}</p>
                    </div>
                    {row.thread_url ? (
                      <a href={row.thread_url} target="_blank" rel="noreferrer" className="text-sm text-sky-700 hover:underline">
                        Open thread
                      </a>
                    ) : null}
                  </div>
                  {row.priority_reason ? <p className="mt-3 text-sm text-slate-700"><span className="font-semibold">Why flagged:</span> {row.priority_reason}</p> : null}
                  {row.suggested_response ? (
                    <div className="mt-3 rounded-2xl border border-sky-200 bg-sky-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Suggested response</p>
                      <p className="mt-2 text-sm text-slate-800 whitespace-pre-wrap">{row.suggested_response}</p>
                      {row.response_reason ? <p className="mt-2 text-xs text-slate-500">{row.response_reason}</p> : null}
                    </div>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {row.action_id ? (
                      <Button
                        size="sm"
                        onClick={() => {
                          setTab("pending");
                          setApprovalTab("dm");
                        }}
                      >
                        Review reply draft
                      </Button>
                    ) : null}
                    {linkedAction && String(linkedAction.status || "") === "approved" ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setTab("pending");
                          setApprovalTab("dm");
                        }}
                      >
                        Open in Direct Messaging
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            }) : <p className="text-sm text-slate-500">No inbound messages flagged yet.</p>}
          </div>
        </div>
      ) : null}

      {!loading && tab === "analytics" ? (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-slate-900">Learning Analytics</h2>
            <p className="mt-1 text-sm text-slate-500">Captured approvals, rewrites, send outcomes, and recent pattern history.</p>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="grid gap-3 md:grid-cols-4">
              <MiniMetric label="Events" value={learning?.totals?.events ?? 0} hint="All captured learning signals." />
              <MiniMetric label="Rewrites" value={learning?.totals?.rewrites ?? 0} hint="Alternative generations requested." />
              <MiniMetric label="Approvals" value={learning?.totals?.approvals ?? 0} hint="Approved actions." />
              <MiniMetric label="Sends" value={learning?.totals?.successful_sends ?? 0} hint="Successful connector sends." />
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Top tones</p>
                <div className="mt-3 space-y-2">
                  {(learning?.tone_counts || []).length ? learning.tone_counts.map((row) => (
                    <div key={row.value} className="flex items-center justify-between text-sm">
                      <span className="text-slate-800">{row.value}</span>
                      <span className="text-slate-500">{row.count}</span>
                    </div>
                  )) : <p className="text-sm text-slate-500">No tone data yet.</p>}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Top focus</p>
                <div className="mt-3 space-y-2">
                  {(learning?.focus_counts || []).length ? learning.focus_counts.map((row) => (
                    <div key={row.value} className="flex items-center justify-between text-sm">
                      <span className="text-slate-800">{row.value}</span>
                      <span className="text-slate-500">{row.count}</span>
                    </div>
                  )) : <p className="text-sm text-slate-500">No focus data yet.</p>}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent events</p>
                <div className="mt-3 space-y-2">
                  {(learning?.recent_events || []).length ? learning.recent_events.slice(0, 8).map((row, idx) => (
                    <div key={`${row.created_at || idx}-${row.event_type}`} className="text-sm">
                      <p className="font-medium text-slate-800">{row.event_type} {row.event_value ? `- ${row.event_value}` : ""}</p>
                      <p className="text-xs text-slate-500">{row.platform}{row.tone ? ` | ${row.tone}` : ""}{row.focus ? ` | ${row.focus}` : ""}</p>
                    </div>
                  )) : <p className="text-sm text-slate-500">No events captured yet.</p>}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "audit" ? (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-slate-900">Audit Trail</h2>
            <p className="mt-1 text-sm text-slate-500">Connector actions, intake events, and content changes captured for review.</p>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="grid gap-3 md:grid-cols-4">
              <label className="text-sm text-slate-700">
                Entity
                <select
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
                  value={auditFilters.entity_type}
                  onChange={(e) => updateAuditFilters({ entity_type: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="action">Action</option>
                  <option value="target">Target</option>
                  <option value="feed_candidate">Feed candidate</option>
                  <option value="platform_account">Platform account</option>
                </select>
              </label>
              <label className="text-sm text-slate-700">
                Action
                <input
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  value={auditFilters.action}
                  onChange={(e) => updateAuditFilters({ action: e.target.value })}
                  placeholder="send, create, rewrite"
                />
              </label>
              <label className="text-sm text-slate-700">
                Platform
                <select
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
                  value={auditFilters.platform}
                  onChange={(e) => updateAuditFilters({ platform: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                </select>
              </label>
              <div className="flex items-end">
                <Button size="sm" variant="secondary" onClick={() => loadAll()}>
                  Refresh
                </Button>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-4">
              <MiniMetric label="Events" value={audit?.totals?.events ?? 0} hint="All audit records captured." />
              <MiniMetric label="Entity types" value={audit?.entity_counts?.length ?? 0} hint="Distinct entities with tracked activity." />
              <MiniMetric label="Actions" value={audit?.action_counts?.length ?? 0} hint="Distinct audit actions." />
              <MiniMetric label="Recent" value={(audit?.recent_events || []).length ?? 0} hint="Events shown in the current window." />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">By entity</p>
                <div className="mt-3 space-y-2">
                  {(audit?.entity_counts || []).length ? audit.entity_counts.map((row) => (
                    <div key={row.value} className="flex items-center justify-between text-sm">
                      <span className="text-slate-800">{row.value}</span>
                      <span className="text-slate-500">{row.count}</span>
                    </div>
                  )) : <p className="text-sm text-slate-500">No audit activity yet.</p>}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">By action</p>
                <div className="mt-3 space-y-2">
                  {(audit?.action_counts || []).length ? audit.action_counts.map((row) => (
                    <div key={row.value} className="flex items-center justify-between text-sm">
                      <span className="text-slate-800">{row.value}</span>
                      <span className="text-slate-500">{row.count}</span>
                    </div>
                  )) : <p className="text-sm text-slate-500">No audit activity yet.</p>}
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent events</p>
              <div className="mt-3 space-y-3">
                {(audit?.recent_events || []).length ? audit.recent_events.slice(0, 10).map((row, idx) => (
                  <div key={`${row.created_at || idx}-${row.action}`} className="border-b border-slate-100 pb-3 last:border-b-0 last:pb-0">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-slate-900">{row.entity_type}</span>
                      <span className="text-slate-500">-</span>
                      <span className="text-slate-800">{row.action}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-600">{row.status}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {row.actor_email || "system"}
                      {row.entity_id ? ` | ${row.entity_id}` : ""}
                      {row.detail ? ` | ${row.detail}` : ""}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {row.created_at}
                    </p>
                  </div>
                )) : <p className="text-sm text-slate-500">No audit events captured yet.</p>}
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "history" ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold text-slate-900">Decision History</h2></CardHeader>
          <CardBody className="space-y-3">
            {historicalActions.length ? historicalActions.map((action) => (
              <div key={action.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <PlatformBadge platform={action.platform} />
                  <span className="text-xs uppercase tracking-wide text-slate-500">{action.action_type}</span>
                  <span className={`text-xs font-medium ${action.status === "approved" ? "text-emerald-700" : "text-rose-700"}`}>{action.status}</span>
                </div>
                <p className="mt-2 text-base font-semibold text-slate-900">{action.title}</p>
                <TargetSummary target={action.target} />
                <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{action.draft_text}</p>
                {action.status === "approved" ? (
                  <SendControls
                    action={action}
                    value={sendOptions[action.id] || { buffer_share_mode: "addToQueue", buffer_due_at: "" }}
                    busy={busyId === action.id}
                    onChange={updateSendOptions}
                    onSend={handleSend}
                  />
                ) : null}
                {action.approved_at ? <p className="mt-2 text-xs text-slate-500">Approved at {action.approved_at}</p> : null}
                {action.sent_at ? <p className="mt-2 text-xs text-slate-500">Sent at {action.sent_at}</p> : null}
                {action.buffer_share_mode ? <p className="mt-2 text-xs text-slate-500">Buffer mode: {action.buffer_share_mode}</p> : null}
                {action.buffer_due_at ? <p className="mt-2 text-xs text-slate-500">Scheduled for {action.buffer_due_at}</p> : null}
                {action.rejected_reason ? <p className="mt-2 text-xs text-slate-500">Rejected reason: {action.rejected_reason}</p> : null}
                {action.send_error ? <p className="mt-2 text-xs text-rose-600">Send error: {action.send_error}</p> : null}
              </div>
            )) : <p className="text-sm text-slate-500">No approval history yet.</p>}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
